import { IImpersonationContext } from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useMetaConnectionQuery } from '../../../../queries/Admin/Meta';
import { Pill } from '../../../../web/components-v2/Pill';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { shadowSm } from '../../../../web/styles/shadows';
import { formatUtcTimestamp } from '../../../containers/adsMeta/utils';

const { colors, common, spacing, typography } = THEME;

interface IProps {
	impersonationContext: IImpersonationContext;
}

// Compact health summary rendered inside the Meta channel section of the Ads
// tab. Mirrors a subset of what `MetaConnectionModal` shows on the global
// operator surface — at-a-glance status pills plus the most-recent
// lead-ingestion and metrics-sync signals. Hidden when no signal exists (the
// 404-synthesize path returns an empty connection for customers that have
// never been connected, and surfacing that as a stack of "no/none" rows is
// noise on the Ads tab).
export function MetaConnectionHealthSection({ impersonationContext }: IProps) {
	const accountId = impersonationContext?.account?.id;
	const connection = useMetaConnectionQuery({ levitateAccountId: accountId, enabled: !!accountId });

	if (!accountId) return null;
	if (!connection.data) return null;

	const data = connection.data;
	const isAdsTeamManaging = data.managedAdAccountIds.length > 0;
	const hasAnySignal =
		data.hasCustomerToken ||
		isAdsTeamManaging ||
		(data.adAccountIds?.length ?? 0) > 0 ||
		!!data.lastPollTimestamp ||
		!!data.lastPollError ||
		!!data.lastLeadReceivedAt ||
		!!data.lastTokenRefreshedAt ||
		!!data.lastMetricsSyncAt ||
		!!data.lastMetricsSyncError;
	if (!hasAnySignal) return null;

	const hasAnyDetail =
		!!data.lastPollTimestamp || !!data.lastLeadReceivedAt || !!data.lastTokenRefreshedAt || !!data.lastMetricsSyncAt;

	return (
		<section className={css(styles.section)} aria-label='Connection health'>
			<h4 className={css(styles.header)}>Connection health</h4>
			<div className={css(styles.card)}>
				<div className={css(styles.pills)}>
					<Pill
						variant='label'
						color={data.hasCustomerToken ? 'green' : 'gray'}
						label={data.hasCustomerToken ? 'Customer OAuth connected' : 'No customer OAuth'}
					/>
					<Pill
						variant='label'
						color={isAdsTeamManaging ? 'green' : 'gray'}
						label={isAdsTeamManaging ? 'Ads team managing' : 'Ads team not managing'}
					/>
				</div>
				{hasAnyDetail ? (
					<div className={css(styles.details)}>
						{data.lastPollTimestamp ? (
							<DetailRow label='Last poll'>
								{formatUtcTimestamp(data.lastPollTimestamp)}
								{typeof data.lastPollLeadCount === 'number'
									? ` · ${data.lastPollLeadCount} ${data.lastPollLeadCount === 1 ? 'lead' : 'leads'}`
									: null}
							</DetailRow>
						) : null}
						{data.lastLeadReceivedAt ? (
							<DetailRow label='Last lead received'>{formatUtcTimestamp(data.lastLeadReceivedAt)}</DetailRow>
						) : null}
						{data.lastTokenRefreshedAt ? (
							<DetailRow label='Token refreshed'>{formatUtcTimestamp(data.lastTokenRefreshedAt)}</DetailRow>
						) : null}
						{data.lastMetricsSyncAt ? (
							<DetailRow label='Last metrics sync'>{formatUtcTimestamp(data.lastMetricsSyncAt)}</DetailRow>
						) : null}
					</div>
				) : null}
				{data.lastPollError ? <ErrorBox label='Last poll error'>{data.lastPollError}</ErrorBox> : null}
				{data.lastMetricsSyncError ? (
					<ErrorBox label={`Metrics sync error · ${formatUtcTimestamp(data.lastMetricsSyncError.timestamp)}`}>
						{data.lastMetricsSyncError.message}
					</ErrorBox>
				) : null}
			</div>
		</section>
	);
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className={css(styles.detailRow)}>
		<span className={css(styles.detailLabel)}>{label}:</span>
		<span>{children}</span>
	</div>
);

const ErrorBox: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className={css(styles.errorBox)}>
		<div className={css(styles.detailRow)}>
			<span className={css(styles.detailLabel)}>{label}</span>
		</div>
		<pre className={css(styles.errorMessage)}>{children}</pre>
	</div>
);

const styles = StyleSheet.create({
	// Outer wrapper — header lives outside the bordered card so "Connection
	// health" reads as a labeled sub-section of the channel section, not as
	// content trapped inside another box.
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		marginTop: spacing.md,
	},
	header: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.md,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		margin: 0,
	},
	// Card chrome mirrors `AccountDetailsNote.container` — white background, a
	// quiet border, a subtle shadow — so the section reads as a peer of the
	// channel's note rather than a darker callout.
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.md,
		padding: spacing.md,
		background: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
		boxShadow: shadowSm,
	},
	pills: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.sm,
	},
	details: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	detailRow: {
		display: 'flex',
		gap: spacing.sm,
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
	},
	detailLabel: {
		color: colors.text.light,
		// Off-scale: column width is sized to fit the longest label ("Last lead
		// received:") without wrapping. Doesn't map to a `THEME.spacing` token
		// because the value is driven by text length, not the spacing rhythm —
		// kept in lockstep with the modal's `detailLabel` for cross-surface
		// visual consistency.
		minWidth: 140,
	},
	errorBox: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: spacing.sm,
		background: colors.danger.light,
		border: `1px solid ${colors.danger.main}`,
		borderRadius: common.borderRadius,
	},
	errorMessage: {
		color: colors.danger.dark,
		fontSize: typography.fontSizes.xs,
		fontFamily: 'monospace',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		margin: 0,
	},
});
